import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Icon from 'flarum/common/components/Icon';
/**
 * How many flames the display has room for.
 *
 * The design always draws five slots and dims the ones the topic has not
 * earned, rather than drawing only the lit ones: a row of two flames next to
 * a row of five is hard to compare at a glance, and the empty slots show
 * where the ceiling is.
 */
const FLAME_SLOTS = 5;
/**
 * The flames: five slots, the ones the topic has earned lit.
 */
export class TopicHeatFlames extends Component {
  view(vnode) {
    const level = this.attrs.level;
    const lit = Math.min(FLAME_SLOTS, Math.max(1, level || 1));
    return m("span", {
      // The tier is on the wrapper, because it is a property of the whole
      // row, not of a single slot: the five flames of one topic are all the
      // same colour, and the colour says which tier the topic reached.
      className: 'TopicHeat-flames TopicHeat-flames--lv' + lit,
      "aria-label": app.translator.trans('ziiven-topics.forum.heat.level', {
        level: lit
      })
    }, Array.from({
      length: FLAME_SLOTS
    }, (_, index) => m("span", {
      className: 'TopicHeat-flame' + (index < lit ? ' TopicHeat-flame--on' : ' TopicHeat-flame--off')
    }, m(Icon, {
      name: "fas fa-fire"
    }))));
  }
}

/**
 * The heat score, as a whole number in a pill.
 */
export class TopicHeatScore extends Component {
  view(vnode) {
    return m("span", {
      className: 'TopicHeat-score' + (this.attrs.high ? ' TopicHeat-score--high' : '')
    }, Math.round(this.attrs.score));
  }
}

/**
 * The trend: up +18% / down 4% / flat.
 *
 * A falling trend shows the absolute value with the arrow carrying the
 * direction, because "down -4%" reads like a double negative.
 */
export class TopicHeatTrend extends Component {
  view(vnode) {
    const trend = this.attrs.trend;
    if (trend === null || trend === undefined) {
      return m("span", {
        className: "TopicHeat-trend TopicHeat-trend--flat"
      }, m(Icon, {
        name: "fas fa-minus"
      }), " ", app.translator.trans('ziiven-topics.forum.heat.flat'));
    }
    const percent = Math.round(Math.abs(trend) * 100);
    if (trend > 0) {
      return m("span", {
        className: "TopicHeat-trend TopicHeat-trend--up"
      }, m(Icon, {
        name: "fas fa-arrow-up"
      }), " +", percent, "%");
    }
    if (trend < 0) {
      return m("span", {
        className: "TopicHeat-trend TopicHeat-trend--down"
      }, m(Icon, {
        name: "fas fa-arrow-down"
      }), " ", percent, "%");
    }

    // Exactly zero: the heat has not moved. That is "flat", not "0%" — and it
    // is not the same as "no history yet" either, but both read as flat and
    // the design only has three states.
    return m("span", {
      className: "TopicHeat-trend TopicHeat-trend--flat"
    }, m(Icon, {
      name: "fas fa-minus"
    }), " ", app.translator.trans('ziiven-topics.forum.heat.flat'));
  }
}

/**
 * The heat of a topic: the flame level, the score and the trend.
 *
 * One component with three shapes, because the design uses the same three
 * pieces in three places: the composer's autocomplete (level + score), the
 * hot topics area (level + score + trend) and the topic page hero (level +
 * score, then the trend beside it).
 *
 * The colours come from CSS custom properties, so a theme can restyle the
 * heat display without touching this file.
 */
export default class TopicHeat extends Component {
  view(vnode) {
    const {
      level,
      score,
      trend,
      badge
    } = this.attrs;
    const flames = m(TopicHeatFlames, {
      level: level
    });
    const scoreBadge = score === null || score === undefined ? null : m(TopicHeatScore, {
      score: score,
      high: level >= 5
    });
    const trendBadge = trend === undefined ? null : m(TopicHeatTrend, {
      trend: trend
    });
    if (badge) {
      return m("span", {
        className: "TopicHeat TopicHeat--badge"
      }, flames, m("span", {
        className: "TopicHeat-badgeLabel"
      }, app.translator.trans('ziiven-topics.forum.heat.score')), scoreBadge);
    }
    return m("span", {
      className: "TopicHeat"
    }, flames, scoreBadge, trendBadge);
  }
}
flarum.reg.add('ziiven-topics', 'forum/components/TopicHeat', TopicHeat);